import * as Yup from "yup";
import React, { Fragment, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import {
  Alert,
  Box,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useSnackbar } from "../../snackbar";
import Iconify from "../../iconify";
import { useResponsive } from "../../../hooks/use-responsive";
// import { data } from "flickity";

export default function ForgotPasswordEmailIntegration() {
  const mdUp = useResponsive("up", "md");

  const SERVER_URI = process.env.REACT_APP_SERVER_URI;
  const AUTHENTIK_LOGIN_URL = `${SERVER_URI}/ssologin`;
  const SEND_OTP_FORGOT_PASS = `${SERVER_URI}/sendOtpForPasswordReset`;
  const VERIFY_OTP_MAIL = `${SERVER_URI}/verifyOtpByMail`;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [emailData, setEmailData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false); // To track OTP state
  const [savedEmail, setSavedEmail] = useState("");
  const [isResendDisabled, setIsResendDisabled] = React.useState(true);
  const [resendTimer, setResendTimer] = React.useState(60);

  const startResendTimer = () => {
    setIsResendDisabled(true);
    setResendTimer(60);
    const timerInterval = setInterval(() => {
      setResendTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(timerInterval);
          setIsResendDisabled(false);
        }
        return prevTimer - 1;
      });
    }, 1000);
  };
  const UpdateEmailSchema = Yup.object().shape({
    // email: Yup.string()
    //   .required("Email is required")
    //   .matches(
    //     /^[a-zA-Z0-9._+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    //     "Email must be a valid email address"
    //   ),

    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Email must be a valid email address"
      )
      .test(
        "is-allowed-domain",
        "Email domain must be one of the following: gmail.com, yahoo.com, outlook.com, protonmail.com, hotmail.com, zoho.com",
        (value) => {
          if (!value) return false;
          const domain = value.split("@")[1];
          return ["gmail.com", "yahoo.com", "outlook.com", "protonmail.com", "mail.com", "zoho.com", "megaconnect.cloud"].includes(domain);
        }
      ),
    otp: Yup.string()
      .min(6, "OTP must be at least 6 characters")
      .matches(
        /^\d{6}$/,
        "OTP must be exactly 6 digits and contain only numbers"
      )
      .required("OTP is required"),
  });

  const defaultValues = {
    email: emailData?.email || "",
    otp: emailData?.otp || "",
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    setError,
    reset,
  } = useForm({
    resolver: yupResolver(UpdateEmailSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const sendOtp = async () => {
    const isEmailValid = await trigger("email");
    // Trigger validation for the email field
    if (!isEmailValid) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    const email = getValues("email");
    setLoading(true);
    // console.log(email);
    try {
      const response = await axios.post(SEND_OTP_FORGOT_PASS, { email });
      setOtpSent(true);
      startResendTimer();
      enqueueSnackbar(response.data.message, { variant: "success" });
      setValue("otp", "");
    } catch (error) {
      console.log("error response", error);
      if (error.response && error.response.status === 409) {
        setError("email", {
          type: "manual",
          message: "Email is already registered.",
        });
      } else if (error.response && error.response.status === 400) {
        setError(error.response.data.message);
      } else if (error.response && error.response.status === 500) {
        setError(error.response.data.message);
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to verify OTP
  const verifyOtp = async (data) => {
    setLoading(true);
    const isEmailValid = await trigger("email");
    const isOtpValid = await trigger("otp");
    // Trigger validation for the email field
    if (!isEmailValid) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!isOtpValid) {
      setErrorMessage("Please enter a valid OTP.");
      return;
    }
    const email = getValues("email");
    const otp = getValues("otp");
    setLoading(true);
    // console.log(email);
    try {
      const response = await axios.post(VERIFY_OTP_MAIL, { email, otp });
      // console.log(response.data)
      enqueueSnackbar(response.data.message, { variant: "success" });
      localStorage.setItem("temptoken", response.data.temptoken);
      setTimeout(() => {
        navigate("/set_new_password"); // Adjust the path as necessary
      }, 3000);
    } catch (error) {
      setErrorMessage("OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const onSubmit = async (data) => {
    // console.log("Form Submitted:", data);
    if (!otpSent) {
      // If OTP is not sent yet, send OTP
      await sendOtp();
    } else {
      // If OTP is sent, verify the OTP
      await verifyOtp();
    }
  };
  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={4} sx={{ mt: 8, mb: 8 }}>
          <Box>
            <Stack direction="column">
              {mdUp && <Typography
                variant="h3"
                sx={{
                  color: "#1D53E7",
                  fontFamily: "Sofia Sans Extra Condensed",
                  fontWeight: 700,
                }}
              >
                ENTER REGISTERED EMAIL{" "}
              </Typography>
              }
              {!mdUp && <Typography
                sx={{
                  color: "#1D53E7",
                  fontSize:28,
                  fontFamily: "Sofia Sans Extra Condensed",
                  fontWeight: 700,
                }}
              >
                ENTER REGISTERED EMAIL{" "}
              </Typography>
              }
            </Stack>
          </Box>
          <Box>
            <Stack direction="column" spacing={1}>
              <Typography
                component="label"
                htmlFor="email_text"
                sx={{
                  fontWeight: 400,
                  fontFamily: "Roboto Flex",
                  fontSize: "16px",
                  color: "#1F1F1F",
                }}
              >
                Email Address
              </Typography>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="email_text"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    // fontFamily='Roboto Flex'
                    // fontSize='18px'
                    // color="#1D53E7"
                    // disabled={otpSent}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                      },
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
          {otpSent && (
            <Box>
              <Stack direction="column" spacing={1} mb={2}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "18px",
                    fontFamily: "Roboto Flex",
                  }}
                >
                  Email Verification
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Roboto Flex",
                    fontWeight: "300",
                    wordWrap: "break-word",
                    color: "GrayText",
                  }}
                >
                  We've sent you an OTP to your email. Please enter the code
                  below to proceed.
                </Typography>
              </Stack>
              <Controller
                name="otp"
                control={control}
                render={({ field }) => (
                  <div>
                    <MuiOtpInput
                      {...field}
                      autoFocus
                      gap={{ xs: 1.5, lg: 3.5 }}
                      length={6}
                      TextFieldsProps={{
                        //   error: !!error,
                        // placeholder: "-",
                        error: !!errors.otp,
                        variant: "outlined",
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                        },
                      }}
                    />

                    {errors.otp && (
                      <FormHelperText sx={{ px: 2 }} error>
                        {errors.otp?.message}
                      </FormHelperText>
                    )}
                  </div>
                )}
              />
              {errorMessage && (
                <Alert
                  variant="outlined"
                  severity="error"
                  sx={{ mt: 1, borderRadius: 3 }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setErrorMessage(null);
                      }}
                    >
                      <Iconify color="red" icon="basil:cross-outline" />
                    </IconButton>
                  }
                >
                  {errorMessage}
                </Alert>
              )}
            </Box>
          )}
          <Box>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              sx={{ textAlign: "center", alignItems: "center" }}
            >
              {otpSent && (
                <LoadingButton
                  size="large"
                  sx={{
                    fontFamily: "Roboto Flex",
                    width: "150px",
                    backgroundColor: "#C7F760",
                    color: "#1E53E8",
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": {
                      backgroundColor: "#1E53E8",
                      color: "#C7F760",
                    },
                  }}
                  type="submit"
                  loading={loading}
                  variant="contained"
                >
                  Verify OTP
                </LoadingButton>
              )}
              {otpSent && (
                <Typography
                  onClick={() => {
                    if (!isResendDisabled) {
                      sendOtp(); // Only call sendOtp when isResendDisabled is false
                    }
                  }}
                  sx={{
                    fontFamily: "Roboto Flex",
                    fontWeight: 700,
                    width: "150px",
                    color: isResendDisabled ? "#b0b0b0" : "#1E53E8",
                    textAlign: { xs: "center", md: "left" },
                    cursor: isResendDisabled ? "not-allowed" : "pointer",
                    textDecoration: "none",

                    "@media (max-width: 600px)": { width: "100%" },
                  }}
                >
                  {isResendDisabled
                    ? `Resend OTP (${resendTimer}s)`
                    : "Resend OTP"}
                </Typography>
              )}
              {!otpSent && (
                <LoadingButton
                  size="large"
                  sx={{
                    fontFamily: "Roboto Flex",
                    width: "150px",
                    backgroundColor: "#C7F760",
                    color: "#1E53E8",
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": {
                      backgroundColor: "#1E53E8",
                      color: "#C7F760",
                    },
                  }}
                   type="submit"
                  loading={loading}
                  variant="contained"
                  onClick={() => {
                    sendOtp();
                  }}
                >
                  Send OTP
                </LoadingButton>
              )}
              {!otpSent && (
                <Typography
                  sx={{
                    // display: "flex",
                    fontSize: 16,
                    fontFamily: "Roboto Flex",
                    fontWeight: "400",
                    wordWrap: "break-word",
                    textAlign: "center",
                    alignItems: "center",
                  }}
                >
                  Already have an account?{" "}
                  <Link
                    to="/user_Login"
                    style={{ color: "#1E53E8" }}
                    // onClick={() => window.open(AUTHENTIK_LOGIN_URL, "_self")}
                  >
                    Log in
                  </Link>
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </form>
    </Fragment>
  );
}
